import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FaMagic, FaCheckCircle, FaInfoCircle, FaArrowRight } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { goReserve } from '../../utils/navigation';
import NoticeContent from '../../components/NoticeContent';
import { useMedicinePrices } from '../../hooks/useMedicinePrices';

interface MedicalSkincarePageProps {
  ipAllowed: boolean;
}

const NOTICE_VERSION = 'v2025-10-01';

const MedicalSkincarePage: React.FC<MedicalSkincarePageProps> = ({ ipAllowed }) => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const lang: 'ja' | 'zh' = i18n.language.toLowerCase().startsWith('zh') ? 'zh' : 'ja';
  const navigate = useNavigate();

  // 薬品価格（DB=admin設定 優先）。セット/単剤とも名称パターンで突合。
  // 成分詳細(MED_DETAILS)や構成説明はDB外のため据え置き。DBに該当が無ければ表示中の定価にフォールバック。
  const { medicines } = useMedicinePrices('skincare', lang);
  const skinPrice = (kw: RegExp): { ex: string; inc: string } | null => {
    const med = medicines.find((m) => kw.test(m.name));
    const sp = med?.price_plans.find((p) => p.plan_type === 'single_purchase');
    return sp
      ? { ex: sp.price_tax_excluded.toLocaleString('ja-JP'), inc: sp.price_tax_included.toLocaleString('ja-JP') }
      : null;
  };

  const L = {
    ja: {
      helmetTitle: 'メディカルスキンケア - Melodis Clinic',
      pageTitle: 'メディカルスキンケア',
      description:
        '内服薬を中心に、体の内側から肌の状態を整える治療です。肝斑・シミ・くすみの改善や、ニキビ・肌荒れの防止など、目的に合わせた処方をご提案します。',
      sectionTitle: 'ご案内',
      badges: ['診察料 0円', '24時間オンライン予約', 'スピード発送', '自由診療（保険適用外）'],
      priceTitle: '価格（診察料 0円）：',
      categorySingle: '【単剤】',
      categorySet: '【セットメニュー】',
      priceNote: '※ 薬剤の組み合わせや服用期間は、医師の判断により調整する場合があります。',
      faqBtn: 'FAQを見る',
      noticeBtn: '注意事項はこちらから',
      treatmentTitle: '治療の流れ・当院の特徴',
      treatments: [
        'オンライン診療で肌の状態や既往歴を確認',
        '医師が最適なセット・薬剤を提案',
        '最短当日から内服開始が可能',
        '副作用や併用禁忌を丁寧にご説明',
      ],
      recommendTitle: 'こんな方におすすめ',
      recommends: [
        '肝斑やシミ、くすみが気になり始めた',
        'ニキビや肌荒れを根本からケアしたい',
        '忙しくて美容皮膚科に通う時間がない',
        '市販薬では効果が実感できなかった',
      ],
      cta: 'オンライン診療を予約する',
      modalTitle: 'メディカルスキンケアの注意事項',
      modalAgree: '上記を読み、内容に同意します。',
      modalCta: '同意して予約に進む',
      modalClose: '閉じる',
    },
    zh: {
      helmetTitle: '医疗级护肤 - Melodis Clinic',
      pageTitle: '医疗级护肤',
      description:
        '以口服药为核心，从体内调理肌肤状态。针对黄褐斑、色斑、暗沉的改善，以及预防痤疮、皮肤粗糙等，为您提供个性化处方方案。',
      sectionTitle: '就诊说明',
      badges: ['诊察费 0 日元', '24 小时在线预约', 'スピード発送', '自费诊疗（不适用医保）'],
      priceTitle: '价格（诊察费 0 日元）：',
      categorySingle: '【单剂】',
      categorySet: '【套餐方案】',
      priceNote: '※ 药物组合与服用期限由医生根据实际情况评估。',
      faqBtn: '查看 FAQ',
      noticeBtn: '查看注意事项',
      treatmentTitle: '就诊流程与本院特点',
      treatments: [
        '在线问诊确认皮肤状态与既往史',
        '医生推荐合适的套餐或药物',
        '最快当日即可开始服药',
        '详细说明不良反应与禁忌事项',
      ],
      recommendTitle: '适合人群',
      recommends: [
        '在意黄褐斑、色斑或皮肤暗沉',
        '想从根本上改善痤疮/皮肤粗糙',
        '忙碌于工作，无暇频繁去美容皮肤科',
        '觉得市卖药效果不明显',
      ],
      cta: '立即预约在线咨询',
      modalTitle: '医疗护肤的注意事项',
      modalAgree: '我已阅读并同意上述内容。',
      modalCta: '同意并继续预约',
      modalClose: '关闭',
    },
  } as const;

  const [noticeOpen, setNoticeOpen] = useState(false);
  const [agree, setAgree] = useState(false);
  const [selectedMed, setSelectedMed] = useState<string | null>(null);

  const MED_DETAILS: Record<string, any> = {
    tranexamic: {
      name: { ja: 'トラネキサム酸（250㎎）', zh: '氨甲环酸 (Tranexamic Acid)' },
      efficacy: {
        ja: 'シミ・肝斑の原因であるメラニンの生成を抑え、透明感のある肌へ導きます。抗炎症作用で色素沈着の予防にも役立ちます。',
        zh: '抑制黑色素生成，从源头改善色斑与黄褐斑。具有抗炎作用，有助于预防和改善炎症后的色素沉着。'
      },
      side_effects: {
        ja: '食欲不振、悪心、嘔吐、胸やけ、眠気、そう痒感、発疹など',
        zh: '食欲不振、恶心、呕吐、烧心、嗜睡、瘙痒、皮疹等'
      },
      usage: {
        ja: '1日2～3回、食後に1回1錠を服用してください。',
        zh: '每日 2～3 次，饭后每次服用 1 粒。'
      },
      contra: {
        ja: '15歳未満、75歳以上の方、トロンビン服用中の方、ピル服用中の方など',
        zh: '15岁以下、75岁以上人群；正在服用凝血酶或避孕药的人群等。'
      },
      image: '/images/meds/tra_250.jpg'
    },
    juvela: {
      name: { ja: 'ユベラ（50㎎）', zh: '维生素 E (Juvela)' },
      efficacy: {
        ja: '抗酸化作用により肌の老化（酸化）を防ぎます。血行を促進し、ターンオーバーを整えることで、シミ・くすみを改善します。',
        zh: '通过抗氧化作用防止肌肤老化。促进血液循环，调理皮肤代谢周期，从而淡化色斑和暗沉。'
      },
      side_effects: {
        ja: '食欲不振、胃部不快感、下痢、便秘、むくみ、発疹など',
        zh: '食欲不振、胃部不适、腹泻、便秘、水肿、皮疹等'
      },
      usage: {
        ja: '1日2～3回、食後に1回1錠を服用してください。',
        zh: '每日 2～3 次，饭后每次服用 1 粒。'
      },
      contra: {
        ja: '15歳未満、75歳以上の方、本剤の成分にアレルギーがある方',
        zh: '15岁以下、75岁以上人群；对本药成分过敏的人群。'
      },
      image: '/images/meds/juvela_50.jpg'
    },
    cinal: {
      name: { ja: 'シナール（1000㎎）', zh: '希那尔 (Cinal)' },
      efficacy: {
        ja: 'メラニン生成を抑えてシミの発生を防ぎます。コラーゲンの生成を促し、肌のハリや潤いを向上させます。',
        zh: '抑制黑色素生成并预防色斑形成。促进胶原蛋白产生，提升肌肤弹性和滋润度。'
      },
      side_effects: {
        ja: '胃部不快感、吐き気、嘔吐、下痢など',
        zh: '胃部不适、恶心、呕吐、腹泻等'
      },
      usage: {
        ja: '1日2～3回、食後に1回1錠を服用してください。',
        zh: '每日 2～3 次，饭后每次服用 1 粒。'
      },
      contra: {
        ja: '15歳未満、75歳以上の方、本剤の成分にアレルギーがある方',
        zh: '15岁以下、75岁以上人群；对本药成分过敏的人群。'
      },
      image: '/images/meds/cinal_1000.jpg'
    },
    cysteine: {
      name: { ja: 'Lシステイン（80㎎）', zh: 'L-半胱氨酸 (L-Cysteine)' },
      efficacy: {
        ja: 'メラニンの排出を助け、シミ・くすみを薄くします。肌の代謝サイクルを正常化し、肌荒れを防ぎます。',
        zh: '辅助黑色素排出，淡化色斑与暗沉。使皮肤代谢周期正常化，预防皮肤粗糙。'
      },
      side_effects: {
        ja: '吐き気、下痢、腹痛など',
        zh: '恶心、腹泻、腹痛等'
      },
      usage: {
        ja: '1日2～3回、食後に1回1錠を服用してください。',
        zh: '每日 2～3 次，饭后每次服用 1 粒。'
      },
      contra: {
        ja: '妊娠中の方、15歳未満、75歳以上の方',
        zh: '妊娠期妇女；15岁以下、75岁以上人群。'
      },
      image: '/images/meds/l_cys_80.jpg'
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setNoticeOpen(false);
    if (noticeOpen) {
      window.addEventListener('keydown', onKey);
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        window.removeEventListener('keydown', onKey);
        document.body.style.overflow = prev;
      };
    }
  }, [noticeOpen]);

  const openNotice = () => {
    setAgree(false);
    setNoticeOpen(true);
  };

  const proceedReserve = () => {
    if (!agree) return;
    setNoticeOpen(false);
    goReserve(navigate, ipAllowed, {
      preselectService: 'skin_care',
      preAgreed: true,
      state: { from: 'service' },
    });
  };

  return (
    <div className="pt-0 sm:pt-10 pb-10 sm:pb-20 bg-[#fff9f8] font-sans" translate="no">
      <Helmet>
        <title>{L[lang].helmetTitle}</title>
        <meta name="description" content={lang === 'ja'
          ? 'メラニン生成を抑えるトラネキサム酸やビタミン類など、医療用医薬品で美肌・美白をサポートします。オンライン診療で処方が可能です。'
          : '通过抑制黑色素生成的氨甲环酸、维生素类等医疗器械用药，全方位辅助美肌美白。支持在线问诊处方。'} />
      </Helmet>

      <div className="container mx-auto px-0 sm:px-4 max-w-5xl">
        <div className="bg-white shadow-none sm:shadow-md rounded-none sm:rounded-3xl p-5 sm:p-8 md:p-12 border-y sm:border border-slate-100">

          {/* Header */}
          <div className="flex items-center mb-6">
            <div className="mr-4 rounded-2xl bg-sky-100 p-3">
              <FaMagic className="text-sky-600 w-7 h-7" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-800">
              <span className="text-emerald-400 mr-2">│</span>
              {L[lang].pageTitle}
            </h1>
          </div>

          <div className="mb-8 flex items-center gap-3 text-left">
            <Link
              to="/about"
              className="inline-flex items-center gap-2 rounded-full bg-slate-50 border border-slate-200 px-4 py-1.5 text-xs md:text-sm text-slate-600 hover:bg-slate-100 transition"
            >
              <span className="w-2 h-2 rounded-full bg-emerald-400" />
              {t('seo.supervision_doctor')}
              <span className="text-[10px] text-slate-400 font-normal ml-1">{t('seo.supervision_title')}</span>
            </Link>
          </div>

          <p className="text-slate-700 text-base md:text-lg leading-relaxed mb-8">
            {L[lang].description}
          </p>

          {/* Section */}
          <section className="rounded-2xl border border-sky-100 bg-[#fff9f8]/60 p-6 md:p-8 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <FaInfoCircle className="text-sky-600 w-5 h-5" />
              <h2 className="text-2xl font-semibold text-sky-700">{L[lang].sectionTitle}</h2>
            </div>

            {/* badges */}
            <ul className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 text-sm">
              {L[lang].badges.map((b, i) => (
                <li key={i} className="rounded-xl border border-sky-200 bg-white px-4 py-3 text-slate-800 text-center">
                  {b}
                </li>
              ))}
            </ul>

            {/* Prices */}
            <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5 md:p-8">
              <h3 className="text-xl font-semibold text-slate-800 mb-6">{L[lang].priceTitle}</h3>
              
              <div className="space-y-8">
                <div>
                  <h4 className="font-bold text-sky-700 mb-3 border-b border-sky-100 pb-1">{L[lang].categorySet}</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="rounded-xl bg-[#fff9f8]/50 p-4 border border-sky-100 flex flex-col">
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-bold text-slate-800">{lang === 'ja' ? '3種セット(肌ケアライト)' : '3种套餐 (护肤轻盈款)'}</span>
                      </div>
                      <div className="text-xs text-slate-500 mb-3 flex flex-wrap gap-x-2 gap-y-1">
                        <button onClick={() => setSelectedMed('juvela')} className="hover:text-sky-600 underline underline-offset-2 decoration-slate-200">
                          {lang === 'ja' ? 'ユベラ (50㎎)' : '维生素 E (50mg)'}
                        </button>
                        <span>/</span>
                        <button onClick={() => setSelectedMed('cinal')} className="hover:text-sky-600 underline underline-offset-2 decoration-slate-200">
                          {lang === 'ja' ? 'シナール (1000㎎)' : '希那尔 (1000mg)'}
                        </button>
                        <span>/</span>
                        <button onClick={() => setSelectedMed('cysteine')} className="hover:text-sky-600 underline underline-offset-2 decoration-slate-200">
                          {lang === 'ja' ? 'L-システイン (80㎎)' : 'L-半胱氨酸 (80mg)'}
                        </button>
                      </div>
                      <div className="mt-auto text-right">
                        <div className="flex items-baseline justify-end gap-1">
                          <span className="font-bold text-lg text-slate-900">{skinPrice(/3種|3种/)?.ex ?? '5,500'}</span>
                          <span className="text-sm text-slate-600">{lang === 'ja' ? '円' : ' 日元'}</span>
                        </div>
                        <div className="text-[11px] text-slate-400 font-normal">
                          ({lang === 'ja' ? '税込' : '含税'} {skinPrice(/3種|3种/)?.inc ?? '6,050'}{lang === 'ja' ? '円' : ' 日元'})
                        </div>
                      </div>
                    </div>
                    <div className="rounded-xl bg-[#fff9f8]/50 p-4 border border-sky-100 flex flex-col">
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-bold text-slate-800">{lang === 'ja' ? '4種セット(肌ケアベーシック)' : '4种套餐 (护肤基础款)'}</span>
                      </div>
                      <div className="text-xs text-slate-500 mb-3 flex flex-wrap gap-x-2 gap-y-1">
                        <button onClick={() => setSelectedMed('tranexamic')} className="hover:text-sky-600 underline underline-offset-2 decoration-slate-200">
                          {lang === 'ja' ? 'トラネキサム酸 (250㎎)' : '氨甲环酸 (250mg)'}
                        </button>
                        <span>/</span>
                        <button onClick={() => setSelectedMed('juvela')} className="hover:text-sky-600 underline underline-offset-2 decoration-slate-200">
                          {lang === 'ja' ? 'ユベラ (50㎎)' : '维生素 E (50mg)'}
                        </button>
                        <span>/</span>
                        <button onClick={() => setSelectedMed('cinal')} className="hover:text-sky-600 underline underline-offset-2 decoration-slate-200">
                          {lang === 'ja' ? 'シナール (1000㎎)' : '希那尔 (1000mg)'}
                        </button>
                        <span>/</span>
                        <button onClick={() => setSelectedMed('cysteine')} className="hover:text-sky-600 underline underline-offset-2 decoration-slate-200">
                          {lang === 'ja' ? 'L-システイン (80㎎)' : 'L-半胱氨酸 (80mg)'}
                        </button>
                      </div>
                      <div className="mt-auto text-right">
                        <div className="flex items-baseline justify-end gap-1">
                          <span className="font-bold text-lg text-slate-900">{skinPrice(/4種|4种/)?.ex ?? '7,200'}</span>
                          <span className="text-sm text-slate-600">{lang === 'ja' ? '円' : ' 日元'}</span>
                        </div>
                        <div className="text-[11px] text-slate-400 font-normal">
                          ({lang === 'ja' ? '税込' : '含税'} {skinPrice(/4種|4种/)?.inc ?? '7,920'}{lang === 'ja' ? '円' : ' 日元'})
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-bold text-sky-700 mb-3 border-b border-sky-100 pb-1">{L[lang].categorySingle}</h4>
                  <ul className="space-y-3 text-slate-700">
                    <li className="flex justify-between items-start flex-wrap gap-2 py-1">
                      <span className="flex flex-col">
                        <button 
                          onClick={() => setSelectedMed('tranexamic')}
                          className="font-bold text-slate-800 hover:text-sky-600 transition flex items-center gap-1 text-left"
                        >
                          {lang === 'ja' ? 'トラネキサム酸（250㎎）' : '氨甲环酸 (Tranexamic Acid)'}
                          <FaInfoCircle className="text-slate-300 w-3 h-3" />
                        </button>
                        <span className="text-[11px] text-slate-500">{lang === 'ja' ? '1日3錠×30日分' : '250mg 每日3粒×30日份'}</span>
                      </span>
                      <div className="text-right">
                        <div className="flex items-baseline gap-1 justify-end">
                          <span className="font-bold text-slate-900">{skinPrice(/トラネキサム|Tranexamic|氨甲环酸/i)?.ex ?? '3,000'}</span>
                          <span className="text-sm text-slate-600">{lang === 'ja' ? '円' : ' 日元'}</span>
                        </div>
                        <div className="text-[10px] text-slate-400 font-normal">
                          ({lang === 'ja' ? '税込' : '含税'} {skinPrice(/トラネキサム|Tranexamic|氨甲环酸/i)?.inc ?? '3,300'}{lang === 'ja' ? '円' : ' 日元'})
                        </div>
                      </div>
                    </li>
                    <li className="flex justify-between items-start flex-wrap gap-2 py-1">
                      <span className="flex flex-col">
                        <button 
                          onClick={() => setSelectedMed('juvela')}
                          className="font-bold text-slate-800 hover:text-sky-600 transition flex items-center gap-1 text-left"
                        >
                          {lang === 'ja' ? 'ユベラ（50㎎）' : '维生素 E (Juvela)'}
                          <FaInfoCircle className="text-slate-300 w-3 h-3" />
                        </button>
                        <span className="text-[11px] text-slate-500">{lang === 'ja' ? '1日3錠×30日分' : '50mg 每日3粒×30日份'}</span>
                      </span>
                      <div className="text-right">
                        <div className="flex items-baseline gap-1 justify-end">
                          <span className="font-bold text-slate-900">{skinPrice(/ユベラ|Juvela|维生素\s*E/i)?.ex ?? '2,000'}</span>
                          <span className="text-sm text-slate-600">{lang === 'ja' ? '円' : ' 日元'}</span>
                        </div>
                        <div className="text-[10px] text-slate-400 font-normal">
                          ({lang === 'ja' ? '税込' : '含税'} {skinPrice(/ユベラ|Juvela|维生素\s*E/i)?.inc ?? '2,200'}{lang === 'ja' ? '円' : ' 日元'})
                        </div>
                      </div>
                    </li>
                    <li className="flex justify-between items-start flex-wrap gap-2 py-1">
                      <span className="flex flex-col">
                        <button 
                          onClick={() => setSelectedMed('cinal')}
                          className="font-bold text-slate-800 hover:text-sky-600 transition flex items-center gap-1 text-left"
                        >
                          {lang === 'ja' ? 'シナール（1000㎎）' : '希那尔 (Cinal)'}
                          <FaInfoCircle className="text-slate-300 w-3 h-3" />
                        </button>
                        <span className="text-[11px] text-slate-500">{lang === 'ja' ? '1日3錠×30日分' : '1000mg 每日3粒×30日份'}</span>
                      </span>
                      <div className="text-right">
                        <div className="flex items-baseline gap-1 justify-end">
                          <span className="font-bold text-slate-900">{skinPrice(/シナール|Cinal|希那尔/i)?.ex ?? '3,000'}</span>
                          <span className="text-sm text-slate-600">{lang === 'ja' ? '円' : ' 日元'}</span>
                        </div>
                        <div className="text-[10px] text-slate-400 font-normal">
                          ({lang === 'ja' ? '税込' : '含税'} {skinPrice(/シナール|Cinal|希那尔/i)?.inc ?? '3,300'}{lang === 'ja' ? '円' : ' 日元'})
                        </div>
                      </div>
                    </li>
                    <li className="flex justify-between items-start flex-wrap gap-2 py-1">
                      <span className="flex flex-col">
                        <button 
                          onClick={() => setSelectedMed('cysteine')}
                          className="font-bold text-slate-800 hover:text-sky-600 transition flex items-center gap-1 text-left"
                        >
                          {lang === 'ja' ? 'Lシステイン（80㎎）' : 'L-半胱氨酸 (L-Cysteine)'}
                          <FaInfoCircle className="text-slate-300 w-3 h-3" />
                        </button>
                        <span className="text-[11px] text-slate-500">{lang === 'ja' ? '1日3錠×30日分' : '80mg 每日3粒×30日份'}</span>
                      </span>
                      <div className="text-right">
                        <div className="flex items-baseline gap-1 justify-end">
                          <span className="font-bold text-slate-900">{skinPrice(/システイン|Cysteine|半胱氨酸/i)?.ex ?? '2,300'}</span>
                          <span className="text-sm text-slate-600">{lang === 'ja' ? '円' : ' 日元'}</span>
                        </div>
                        <div className="text-[10px] text-slate-400 font-normal">
                          ({lang === 'ja' ? '税込' : '含税'} {skinPrice(/システイン|Cysteine|半胱氨酸/i)?.inc ?? '2,530'}{lang === 'ja' ? '円' : ' 日元'})
                        </div>
                      </div>
                    </li>
                    <li className="flex justify-between items-start flex-wrap gap-2 py-1">
                      <span className="flex flex-col">
                        <span className="font-bold text-slate-800">{lang === 'ja' ? '十味敗毒湯250mg' : '十味败毒汤 (Jumihaidokuto)'}</span>
                        <span className="text-[11px] text-slate-500">{lang === 'ja' ? '1日2包×30日分' : '每日2包×30日份'}</span>
                      </span>
                      <div className="text-right">
                        <div className="flex items-baseline gap-1 justify-end">
                          <span className="font-bold text-slate-900">{skinPrice(/十味敗毒|十味败毒|Jumihaidoku/i)?.ex ?? '6,000'}</span>
                          <span className="text-sm text-slate-600">{lang === 'ja' ? '円' : ' 日元'}</span>
                        </div>
                        <div className="text-[10px] text-slate-400 font-normal">
                          ({lang === 'ja' ? '税込' : '含税'} {skinPrice(/十味敗毒|十味败毒|Jumihaidoku/i)?.inc ?? '6,600'}{lang === 'ja' ? '円' : ' 日元'})
                        </div>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>

              {/* links */}
              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  onClick={openNotice}
                  className="inline-flex items-center gap-2 rounded-full border border-rose-300 bg-rose-50 px-6 py-2.5 text-sm font-medium text-rose-700 hover:bg-rose-100 transition"
                >
                  {L[lang].noticeBtn} <FaArrowRight className="w-4 h-4" />
                </button>
              </div>

              <p className="mt-4 text-slate-500 text-xs">{L[lang].priceNote}</p>
            </div>
          </section>

          {/* Treatment list */}
          <div className="rounded-2xl p-6 md:p-8 mb-8 border border-sky-100 bg-[#fff9f8]/60">
            <h2 className="text-2xl font-semibold text-sky-700 mb-4">{L[lang].treatmentTitle}</h2>
            <div className="flex flex-wrap gap-3">
              {L[lang].treatments.map((item, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-white px-4 py-2 text-sm text-slate-800"
                >
                  <FaCheckCircle className="text-sky-500 w-4 h-4" />
                  {item}
                </span>
              ))}
            </div>
          </div>

          {/* Recommend list */}
          <div className="rounded-2xl p-6 md:p-8 bg-slate-50 border border-slate-100">
            <h3 className="text-xl font-semibold text-slate-800 mb-4">{L[lang].recommendTitle}</h3>
            <ul className="grid md:grid-cols-2 gap-3">
              {L[lang].recommends.map((item, idx) => (
                <li
                  key={idx}
                  className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-700"
                >
                  <FaCheckCircle className="text-sky-500 w-5 h-5 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* CTA */}
          {isAuthenticated && (
            <div className="mt-10 text-center">
              <button
                onClick={openNotice}
                className="inline-block bg-sky-600 hover:bg-sky-600 active:bg-sky-700 text-white px-10 py-3.5 text-base md:text-lg rounded-full font-semibold shadow-sm transition"
              >
                {L[lang].cta}
              </button>
              <div className="mx-auto mt-3 h-1 w-20 rounded-full bg-sky-300/60" />
            </div>
          )}
        </div>
      </div>

      {/* 注意事項モーダル */}
      {noticeOpen && (
        <div
          className="fixed inset-0 z-[1000] overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="notice-title"
          onClick={() => setNoticeOpen(false)}
        >
          <div className="fixed inset-0 bg-black/40" />
          <div
            className="min-h-full flex items-start md:items-center justify-center p-4 md:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative z-[1001] w-full max-w-2xl bg-white rounded-2xl shadow-xl p-6 md:p-7 border border-slate-200 max-h-[90vh] overflow-y-auto">
              <div className="flex items-start justify-between gap-4 mb-3">
                <h3 id="notice-title" className="text-xl md:text-2xl font-semibold text-slate-900">
                  {L[lang].modalTitle}
                </h3>
                <button
                  onClick={() => setNoticeOpen(false)}
                  aria-label="Close"
                  className="rounded-full px-3 py-1.5 bg-slate-100 text-slate-600 hover:bg-slate-200"
                >
                  ×
                </button>
              </div>

              <NoticeContent service="skin_care" lang={i18n.language} version={NOTICE_VERSION} />

              <label className="mt-4 flex items-start gap-2 text-slate-700">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                />
                <span>{L[lang].modalAgree}</span>
              </label>

              <div className="mt-5 flex flex-wrap gap-3">
                <button
                  disabled={!agree}
                  onClick={proceedReserve}
                  className={`inline-flex items-center justify-center rounded-full px-6 py-2.5 font-semibold text-white transition ${agree ? 'bg-sky-600 hover:bg-sky-600 active:bg-sky-700' : 'bg-sky-300 cursor-not-allowed'
                    }`}
                >
                  {L[lang].modalCta}
                </button>
                <button
                  onClick={() => setNoticeOpen(false)}
                  className="rounded-full border border-slate-300 px-6 py-2.5 text-slate-700 hover:bg-slate-50"
                >
                  {L[lang].modalClose}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 药品详情モーダル */}
      {selectedMed && MED_DETAILS[selectedMed] && (
        <div
          className="fixed inset-0 z-[1100] overflow-y-auto"
          role="dialog"
          aria-modal="true"
          onClick={() => setSelectedMed(null)}
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            className="min-h-full flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative z-[1101] w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
              {/* Image Area */}
              <div className="relative h-48 bg-slate-100">
                <img 
                  src={MED_DETAILS[selectedMed].image} 
                  alt={MED_DETAILS[selectedMed].name[lang]} 
                  className="w-full h-full object-contain p-4"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'https://placehold.co/400x300?text=No+Image';
                  }}
                />
                <button
                  onClick={() => setSelectedMed(null)}
                  className="absolute top-4 right-4 h-10 w-10 flex items-center justify-center rounded-full bg-black/10 text-slate-600 hover:bg-black/20"
                >
                  ×
                </button>
              </div>

              <div className="p-6 md:p-8">
                <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-6">
                  {MED_DETAILS[selectedMed].name[lang]}
                </h3>

                <div className="space-y-6">
                  <div>
                    <h4 className="text-xs font-black uppercase tracking-wider text-sky-600 mb-2">
                       {lang === 'ja' ? '効果・効能' : '效果与功能'}
                    </h4>
                    <p className="text-sm text-slate-700 leading-relaxed">
                      {MED_DETAILS[selectedMed].efficacy[lang]}
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xs font-black uppercase tracking-wider text-sky-600 mb-2">
                       {lang === 'ja' ? '用法・用量' : '用法用量'}
                    </h4>
                    <p className="text-sm text-slate-700 leading-relaxed">
                      {MED_DETAILS[selectedMed].usage[lang]}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-xs font-black uppercase tracking-wider text-amber-600 mb-2">
                         {lang === 'ja' ? '副作用' : '副作用'}
                      </h4>
                      <p className="text-xs text-slate-600 leading-relaxed">
                        {MED_DETAILS[selectedMed].side_effects[lang]}
                      </p>
                    </div>
                    <div>
                      <h4 className="text-xs font-black uppercase tracking-wider text-rose-600 mb-2">
                         {lang === 'ja' ? '処方できない方' : '禁忌人群'}
                      </h4>
                      <p className="text-xs text-slate-600 leading-relaxed">
                        {MED_DETAILS[selectedMed].contra[lang]}
                      </p>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => setSelectedMed(null)}
                  className="mt-8 w-full py-4 bg-slate-900 text-white rounded-2xl font-bold hover:bg-slate-800 transition shadow-lg"
                >
                  {L[lang].modalClose}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MedicalSkincarePage;
